import {toast} from 'react-toastify'
import {IntlShape} from 'react-intl'

export interface CarrierImportUnmatchedRow {
  rowNumber: number
  memberId: string
  name: string
  reason: string
  rawRowJson: string | null
  carrierStatusRaw?: string | null
  carrierStatusGeneric?: string | null
  systemPolicyStatus?: string | null
  systemAgentName?: string | null
  systemAgentEmail?: string | null
}

export const exportUnmatchedCsv = (
  intl: IntlShape,
  unmatchedRows: CarrierImportUnmatchedRow[],
  statusTitleByCode: Record<string, string>,
  filenamePrefix = 'unmatched_rows',
  headerOrder?: string[]
) => {
  const rawRows: Record<string, string | null>[] = []
  const reasons: string[] = []
  const carrierStatusRawList: string[] = []
  const carrierStatusGenericList: string[] = []
  const systemPolicyStatusTitleList: string[] = []
  const systemAgentNameList: string[] = []
  const systemAgentEmailList: string[] = []
  const headerSet = new Set<string>()

  for (const row of unmatchedRows) {
    if (!row.rawRowJson) continue
    try {
      const parsed = JSON.parse(row.rawRowJson) as Record<string, string | null>
      Object.keys(parsed).forEach((k) => {
        if (k === 'row_number') return
        headerSet.add(k)
      })
      rawRows.push(parsed)
      reasons.push(row.reason || '')
      carrierStatusRawList.push(row.carrierStatusRaw || '')
      carrierStatusGenericList.push(row.carrierStatusGeneric || '')
      systemPolicyStatusTitleList.push(
        (row.systemPolicyStatus && statusTitleByCode[row.systemPolicyStatus]) || ''
      )
      systemAgentNameList.push(row.systemAgentName || '')
      systemAgentEmailList.push(row.systemAgentEmail || '')
    } catch {
      // skip unparseable rows
    }
  }

  if (rawRows.length === 0) {
    toast.warning(
      intl.formatMessage({
        id: 'policies.list.carrierImport.toast.noRawData',
        defaultMessage: 'No raw data available to export',
      })
    )
    return
  }

  const headersInFile = Array.from(headerSet)
  const orderedFromFile = (headerOrder || [])
    .filter((h) => h && h !== 'row_number')
    .filter((h) => headerSet.has(h))
  const remaining = headersInFile.filter((h) => !orderedFromFile.includes(h))
  const headers = orderedFromFile.length > 0 ? [...orderedFromFile, ...remaining] : headersInFile
  const extraHeaders = [
    'Carrier Status Raw',
    'Carrier Status Generic',
    'System Status',
    'System Agent',
    'System Agent Email',
    'Observaciones',
  ]
  const allHeaders = [...headers, ...extraHeaders]
  const csvLines: string[] = []

  csvLines.push(allHeaders.map((h) => `"${h.replace(/"/g, '""')}"`).join(','))

  for (let i = 0; i < rawRows.length; i++) {
    const row = rawRows[i]
    const line = headers.map((h) => {
      const val = row[h] ?? ''
      return `"${String(val).replace(/"/g, '""')}"`
    })
    line.push(`"${(carrierStatusRawList[i] || '').replace(/"/g, '""')}"`)
    line.push(`"${(carrierStatusGenericList[i] || '').replace(/"/g, '""')}"`)
    line.push(`"${(systemPolicyStatusTitleList[i] || '').replace(/"/g, '""')}"`)
    line.push(`"${(systemAgentNameList[i] || '').replace(/"/g, '""')}"`)
    line.push(`"${(systemAgentEmailList[i] || '').replace(/"/g, '""')}"`)
    line.push(`"${(reasons[i] || '').replace(/"/g, '""')}"`)
    csvLines.push(line.join(','))
  }

  const csvContent = csvLines.join('\n')
  const blob = new Blob([csvContent], {type: 'text/csv;charset=utf-8;'})
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = `${filenamePrefix}_${new Date().toISOString().slice(0, 10)}.csv`
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}
